import type {AudioCapture} from './AssistantAudioCapture.types';
// Local browser types avoid introducing DOM globals into the React Native TS project.
type Stream = {getTracks(): {stop(): void}[]};
type Chunk = {size: number};
type Recorder = {state: string; mimeType: string; start(): void; stop(): void;
  ondataavailable: ((event: {data: Chunk}) => void) | null;
  onstop: (() => void) | null; onerror: (() => void) | null};
const browser = globalThis as unknown as {
  navigator?: {mediaDevices?: {getUserMedia(options: {audio: boolean}): Promise<Stream>}};
  MediaRecorder?: {new(stream: Stream, options?: {mimeType: string}): Recorder; isTypeSupported(mime: string): boolean};
  Blob: new(chunks: Chunk[], options: {type: string}) => Chunk;
  FormData: new() => FormData & {append(name: string, blob: Chunk, filename: string): void};
};
export const audioCaptureSupported = () => !!browser.navigator?.mediaDevices?.getUserMedia && !!browser.MediaRecorder;
export function createAudioCapture(): AudioCapture {
  let stream: Stream | null = null;
  let recorder: Recorder | null = null;
  let disposed = false;
  let chunks: Chunk[] = [];
  return {
    async start() {
      if (!browser.navigator?.mediaDevices || !browser.MediaRecorder) throw new Error('unsupported');
      stream = await browser.navigator.mediaDevices.getUserMedia({audio: true});
      if (disposed) {stream.getTracks().forEach(t => t.stop()); return;}
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(m => browser.MediaRecorder!.isTypeSupported(m));
      recorder = new browser.MediaRecorder(stream, mimeType ? {mimeType} : undefined);
      recorder.ondataavailable = e => {if (e.data.size) chunks.push(e.data);};
      recorder.start();
    },
    async stop() {
      if (!recorder || recorder.state !== 'recording') throw new Error('recording');
      const current = recorder;
      await new Promise<void>((resolve, reject) => {
        current.onstop = () => resolve();
        current.onerror = () => reject(new Error('recording'));
        current.stop();
      });
      stream?.getTracks().forEach(t => t.stop());
      const mime = current.mimeType || 'audio/webm';
      const blob = new browser.Blob(chunks, {type: mime});
      if (blob.size < 512) throw new Error('short');
      const data = new browser.FormData();
      data.append('audio', blob, `assistant.${mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : 'webm'}`);
      return data;
    },
    async dispose() {
      disposed = true;
      if (recorder?.state === 'recording') recorder.stop();
      stream?.getTracks().forEach(t => t.stop());
      chunks = [];
    },
  };
}
